/**
 * ZoneBoundaryLayer — interactive zone polygon layer for A1-S3.
 *
 * Renders on top of DensityHeatmap (which sets interactive:false on its polygons).
 * Each zone gets:
 *   - A border-only polygon coloured by ZoneStatus (normal/warning/alert)
 *   - fillOpacity:0.01 so the full interior area is a pointer-event hit target
 *   - A click handler that opens the ZoneStatusPanel (the single place with full zone detail)
 *   - A reticle on the zone and a translucent data tag: name, live load, trend, persons, and two boxed indicators
 *     (active alerts, crowd heading). Tags stand in tidy columns beside the venue so they never cover a zone, each
 *     joined to its reticle by a short leader (horizontal run, 45 degree finish). Every tag can be closed.
 *     Placement is solved by utils/zone-card-layout.ts.
 *   - Tags are shown for a few seconds when the map lands (and whenever the zone set or the Zone tags layer changes),
 *     then fade out so the map is clear. Hovering or selecting a zone brings its tag back; the reticle always stays.
 *
 * Colour semantics are shared with the zone panel and the top bar (utils/zone-status.ts):
 *   load / zone status  green - amber - ember     active alerts  critical red     crowd heading  teal
 *
 * No PII — all values are anonymous aggregates.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Polygon, Marker, Tooltip, useMap } from 'react-leaflet';
import L, { type LatLngTuple, type Map as LeafletMap } from 'leaflet';
import type { DensitySnapshot, Zone } from '@vigilo/shared';
import type { ColorPalette } from '@vigilo/design-system';
import { alertCountColor, loadColor, resolveZoneStatus, trendColor, zoneStatusColor, zoneStatusFromUtil } from '../utils/zone-status';
import { withAlpha } from '../utils/color';
import {
  solveCardLayout,
  type CardPlacement,
  type CardSize,
  type CardZoneInput,
  type Pt,
  type Rect,
} from '../utils/zone-card-layout';

// ── Helpers ────────────────────────────────────────────────────────────────────

type ZoneSnapshot = DensitySnapshot & { dominantDirection?: number | null };

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const FONT = "'Albert Sans', sans-serif";

/** Compass bearing in degrees (0 = north, clockwise) to an 8-point label, or null when unknown. */
export function compassLabel(bearing: number | null | undefined): string | null {
  if (bearing === null || bearing === undefined || !Number.isFinite(bearing)) return null;
  return COMPASS_POINTS[Math.round((((bearing % 360) + 360) % 360) / 45) % 8];
}

function escapeHtml(value: string): string {
  const entities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return value.replace(/[&<>"']/g, ch => entities[ch]);
}

function statusBorderColor(zone: Zone, snap: DensitySnapshot | undefined, C: ColorPalette): string {
  if (zone.archivedAt) return C.muted;
  // The border follows live utilisation, like the fill does. The catalogue `zone.status` is never
  // updated by the runtime, so using it here left the border green next to a red fill.
  return zoneStatusColor(resolveZoneStatus(null, snap), C);
}

/** Outer ring without the repeated closing vertex GeoJSON polygons carry. */
function openRing(ring: number[][]): number[][] {
  if (ring.length > 1) {
    const first = ring[0], last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return ring.slice(0, -1);
  }
  return ring;
}

/** Area-weighted centroid, [lat, lng]. (Averaging vertices would count the closing vertex twice and lean towards it.) */
export function polygonCentroid(coords: number[][][]): LatLngTuple {
  const ring = openRing(coords[0]);
  const [ox, oy] = ring[0];
  let area = 0, cx = 0, cy = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i], q = ring[(i + 1) % ring.length];
    const px = p[0] - ox, py = p[1] - oy, qx = q[0] - ox, qy = q[1] - oy;
    const cross = px * qy - qx * py;
    area += cross;
    cx += (px + qx) * cross;
    cy += (py + qy) * cross;
  }
  if (Math.abs(area) < 1e-18) {
    let lat = 0, lng = 0;
    for (const [x, y] of ring) { lng += x; lat += y; }
    return [lat / ring.length, lng / ring.length];
  }
  return [oy + cy / (3 * area), ox + cx / (3 * area)];
}

function toLeafletRings(coordinates: number[][][]): LatLngTuple[][] {
  return coordinates.map(ring =>
    ring.map(([lng, lat]) => [lat, lng] as LatLngTuple)
  );
}

// ── Tag content + markup ───────────────────────────────────────────────────────

interface CardContent {
  id:          string;
  name:        string;
  util:        number | null;
  persons:     number | null;
  trend:       string | null;
  alerts:      number;
  bearing:     number | null;
  borderColor: string;
}

type SizeMap = Record<CardSize, { w: number; h: number }>;

const NAME_MAX_FULL_PX = 112;
const NAME_MAX_COMPACT_PX = 82;
const NAME_CHAR_PX = 6.3;

/** First guess at tag size; the real size is measured from the DOM after first paint and fed back in. */
function estimateSizes(c: CardContent): SizeMap {
  const lines = (max: number) => Math.min(3, Math.ceil((c.name.length * NAME_CHAR_PX) / max));
  return {
    full: { w: 176, h: 17 + 12 * lines(NAME_MAX_FULL_PX) + 7 + 18 + 8 + 20 },
    compact: { w: 132, h: 15 + 12 * lines(NAME_MAX_COMPACT_PX) + 6 + 18 + 7 + 20 },
  };
}

function headingArrowSvg(bearing: number, color: string, size = 10): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 10 10" style="transform:rotate(${bearing}deg);display:block;flex:none;"><path d="M5 0 L9 9 L5 7 L1 9 Z" fill="${color}"/></svg>`;
}

function box(color: string, inner: string): string {
  return `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 7px;border-radius:4px;border:1px solid ${withAlpha(color, 0.6)};background:${withAlpha(color, 0.12)};color:${color};font-size:10px;font-weight:800;letter-spacing:0.04em;white-space:nowrap;">${inner}</span>`;
}

const TREND_GLYPH: Record<string, string> = { rising: '&#8593;', falling: '&#8595;', stable: '&#8594;' };

interface CalloutState {
  hovered:  boolean;
  selected: boolean;
  /** Tag (card + leader) is on screen. Reticles are always on screen. */
  shown:    boolean;
  /** Map is mid zoom: everything hides until it settles. */
  fading:   boolean;
  /** The zone name label under the reticle is allowed (the Zone tags layer is on). */
  labelled: boolean;
}

/** How long tags stay up after the map lands. */
export const TAG_AUTO_HIDE_MS = 10000;
/** The map and the panel fade in over this long; the count of TAG_AUTO_HIDE_MS starts after it. */
const REVEAL_FADE_IN_MS = 450;

// Hover / selection / reveal are driven by classes on elements that already exist, so the CSS can animate them.
// (Re-creating a Leaflet divIcon replaces the DOM node, which would cut every transition short.)
const CALLOUT_CSS = `
.crowd-zone-callout .zc-card-group{opacity:0;visibility:hidden;transition:opacity .6s cubic-bezier(.4,0,.2,1),visibility 0s linear .6s}
.crowd-zone-callout .zc-card-group.on{opacity:1;visibility:visible;transition:opacity .5s cubic-bezier(.2,0,.2,1),visibility 0s}
.crowd-zone-callout.zoom .zc-card-group{opacity:0!important;transition:opacity .4s ease,visibility 0s linear .4s}
.crowd-zone-callout .zc-tag{cursor:pointer}
.leaflet-tooltip.crowd-hover-tip{background:transparent;border:0;box-shadow:none;padding:0;opacity:1}
.leaflet-tooltip.crowd-hover-tip::before{display:none}
.crowd-zone-callout .zc-name{transition:opacity .6s ease .3s}
.crowd-zone-callout.hc .zc-card-group.on ~ .zc-name{opacity:0}
.crowd-zone-callout.nolabel .zc-name{opacity:0!important}
.crowd-zone-callout .zc-tag{transition:background .2s ease,border-color .2s ease,box-shadow .2s ease}
.crowd-zone-callout.hot .zc-tag{background:var(--zc-glass-hi)!important;border-color:var(--zc-border-hi) var(--zc-border-hi) var(--zc-border-hi) var(--zc-color)!important}
.crowd-zone-callout.sel .zc-tag{box-shadow:0 0 0 1.5px var(--zc-ring),0 6px 18px rgba(0,0,0,.32)!important}
.crowd-zone-callout.hot .zc-lead{stroke-width:2!important;stroke-opacity:1!important}
.crowd-zone-callout.hot .zc-lead-halo{stroke-width:4.5!important}
.crowd-zone-callout .zc-reticle-ring{transform-box:fill-box;transform-origin:center;transition:transform .2s ease}
.crowd-zone-callout.hot .zc-reticle-ring{transform:scale(1.23)}
@media (prefers-reduced-motion:reduce){.crowd-zone-callout .zc-card-group,.crowd-zone-callout .zc-tag,.crowd-zone-callout .zc-reticle-ring{transition:none}}
.leaflet-overlay-pane path.leaflet-interactive:focus{outline:none}
.crowd-zone-sel-glow{pointer-events:none;animation:zsIn .9s ease-out both,zsGlow 5s cubic-bezier(.37,0,.63,1) .9s infinite}
.crowd-zone-sel-ring{pointer-events:none;transform-box:fill-box;transform-origin:center;animation:zsIn .9s ease-out both,zsShrink 5s cubic-bezier(.37,0,.63,1) .9s infinite}
@keyframes zsIn{from{opacity:0}to{opacity:1}}
@keyframes zsGlow{0%,100%{stroke-opacity:.32;filter:drop-shadow(0 0 1.5px var(--zs-color,currentColor))}50%{stroke-opacity:.6;filter:drop-shadow(0 0 4px var(--zs-color,currentColor))}}
@keyframes zsShrink{0%{transform:scale(1.04);stroke-opacity:0}35%{stroke-opacity:.3}85%,100%{transform:scale(1);stroke-opacity:0}}
@media (prefers-reduced-motion:reduce){.crowd-zone-sel-glow{animation:none;stroke-opacity:.45}.crowd-zone-sel-ring{animation:none;stroke-opacity:0}}
`;

export function ensureCalloutStyles(): void {
  if (typeof document === 'undefined' || document.getElementById('crowd-zone-callout-css')) return;
  const el = document.createElement('style');
  el.id = 'crowd-zone-callout-css';
  el.textContent = CALLOUT_CSS;
  document.head.appendChild(el);
}

/** Class list for the state that is baked into freshly created markup. */
function stateClasses(st: CalloutState): string {
  return [st.hovered || st.selected ? 'hot' : '', st.selected ? 'sel' : '', st.fading ? 'zoom' : '', st.labelled ? '' : 'nolabel'].filter(Boolean).join(' ');
}

function tagHtml(p: CardPlacement, c: CardContent, C: ColorPalette): string {
  const compass = compassLabel(c.bearing);
  const compact = p.size === 'compact';
  const load = c.util === null ? C.muted : loadColor(c.util, C);
  const alertColor = alertCountColor(C);

  const alertBox = c.alerts > 0
    ? box(alertColor, compact ? `&#9650; ${c.alerts}` : `&#9650; ${c.alerts} ${c.alerts === 1 ? 'ALERT' : 'ALERTS'}`)
    : box(C.green, '&#10003; CLEAR');
  const headingBox = compass && c.bearing !== null
    ? box(C.teal, `${headingArrowSvg(c.bearing, C.teal)}${compass}`)
    : box(C.muted, 'HEADING &#8212;');

  const nameEl = `<span style="flex:1;min-width:0;max-width:${compact ? NAME_MAX_COMPACT_PX : NAME_MAX_FULL_PX}px;font-size:10px;line-height:12px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;white-space:normal;overflow-wrap:anywhere;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;overflow:hidden;color:${C.text};">${escapeHtml(c.name)}</span>`;
  const dot = `<span style="width:8px;height:8px;border-radius:50%;background:${c.borderColor};flex:none;margin-top:2px;"></span>`;
  const close = `<button type="button" data-zone-close="${escapeHtml(c.id)}" aria-label="Hide ${escapeHtml(c.name)} tag" title="Hide tag" style="pointer-events:auto;cursor:pointer;flex:none;width:22px;height:22px;margin:-5px -7px 0 0;padding:0;display:flex;align-items:center;justify-content:center;border:0;border-radius:4px;background:transparent;color:${C.sub};font-size:16px;line-height:1;font-family:inherit;">&times;</button>`;
  const header = `<div style="display:flex;align-items:flex-start;gap:7px;">${dot}${nameEl}${close}</div>`;

  const value = c.util === null
    ? `<span style="font-size:10px;color:${C.muted};">Awaiting data</span>`
    : `<span style="font-size:18px;font-weight:800;line-height:1;color:${load};">${Math.round(c.util)}%</span>`
      + (c.trend && TREND_GLYPH[c.trend] ? `<span style="font-size:14px;font-weight:800;line-height:1;color:${trendColor(c.trend, C)};">${TREND_GLYPH[c.trend]}</span>` : '')
      + (!compact && c.persons !== null ? `<span style="font-size:10px;color:${C.sub};margin-left:auto;">${c.persons.toLocaleString()} persons</span>` : '');

  const valueRow = `<div style="display:flex;align-items:baseline;gap:6px;margin-top:${compact ? 6 : 7}px;">${value}</div>`;
  const boxRow = `<div style="display:flex;align-items:center;gap:6px;margin-top:${compact ? 7 : 8}px;">${alertBox}${headingBox}</div>`;

  const left = p.card.x - p.anchor.x;
  const top = p.card.y - p.anchor.y;

  return `<div class="zc-tag" data-zone-card="${escapeHtml(c.id)}" data-card-size="${p.size}" style="position:absolute;left:${left}px;top:${top}px;pointer-events:auto;box-sizing:border-box;min-width:${compact ? 118 : 150}px;max-width:190px;font-family:${FONT};padding:8px 10px 9px;background:var(--zc-glass);-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);border:1px solid var(--zc-border);border-left:3px solid var(--zc-color);border-radius:8px;box-shadow:0 6px 18px rgba(0,0,0,0.32);white-space:nowrap;width:max-content;">${header}${valueRow}${boxRow}</div>`;
}

/**
 * The same facts as a zone's card, shown beside the pointer while it rests on the zone, so an operator reads a zone
 * without moving their eyes to a card elsewhere on the map.
 */
function hoverHtml(c: CardContent, C: ColorPalette): string {
  const compass = compassLabel(c.bearing);
  const load = c.util === null ? C.muted : loadColor(c.util, C);
  const alertBox = c.alerts > 0
    ? box(alertCountColor(C), `&#9650; ${c.alerts} ${c.alerts === 1 ? 'ALERT' : 'ALERTS'}`)
    : box(C.green, '&#10003; CLEAR');
  const headingBox = compass && c.bearing !== null
    ? box(C.teal, `${headingArrowSvg(c.bearing, C.teal)}${compass}`)
    : box(C.muted, 'HEADING &#8212;');
  const value = c.util === null
    ? `<span style="font-size:10px;color:${C.muted};">Awaiting data</span>`
    : `<span style="font-size:18px;font-weight:800;line-height:1;color:${load};">${Math.round(c.util)}%</span>`
      + (c.trend && TREND_GLYPH[c.trend] ? `<span style="font-size:14px;font-weight:800;line-height:1;color:${trendColor(c.trend, C)};">${TREND_GLYPH[c.trend]}</span>` : '')
      + (c.persons !== null ? `<span style="font-size:10px;color:${C.sub};margin-left:auto;padding-left:10px;">${c.persons.toLocaleString()} persons</span>` : '');
  return `<div style="box-sizing:border-box;min-width:170px;max-width:230px;font-family:${FONT};padding:8px 11px 9px;background:${withAlpha(C.surface, 0.97)};border:1px solid ${withAlpha(C.border2, 0.9)};border-left:3px solid ${c.borderColor};border-radius:8px;box-shadow:0 8px 22px rgba(0,0,0,0.38);white-space:nowrap;">`
    + `<div style="display:flex;align-items:center;gap:7px;"><span style="width:8px;height:8px;border-radius:50%;background:${c.borderColor};flex:none;"></span><span style="font-size:10px;line-height:12px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;white-space:normal;color:${C.text};">${escapeHtml(c.name)}</span></div>`
    + `<div style="display:flex;align-items:baseline;gap:6px;margin-top:7px;">${value}</div>`
    + `<div style="display:flex;align-items:center;gap:6px;margin-top:8px;">${alertBox}${headingBox}</div>`
    + `</div>`;
}

/** Attach -> anchor as a horizontal (or vertical) run that finishes with a 45 degree stroke, anchor at 0,0. */
function leaderPoints(ax: number, ay: number): string {
  const adx = Math.abs(ax), ady = Math.abs(ay);
  if (adx >= ady) {
    const jx = Math.sign(ax) * ady;
    return `${ax},${ay} ${jx},${ay} 0,0`;
  }
  const jy = Math.sign(ay) * adx;
  return `${ax},${ay} ${ax},${jy} 0,0`;
}

function reticleHtml(c: CardContent, C: ColorPalette): string {
  const col = c.borderColor;
  const r = 6.5;
  const calm = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const pulse = c.alerts > 0 && !calm
    ? `<circle r="7" fill="none" stroke="${alertCountColor(C)}" stroke-width="1.5"><animate attributeName="r" values="7;15" dur="1.8s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.75;0" dur="1.8s" repeatCount="indefinite"/></circle>`
    : '';
  return `<svg width="1" height="1" style="position:absolute;left:0;top:0;overflow:visible;">${pulse}`
    + `<g class="zc-reticle-ring"><circle r="${r}" fill="${withAlpha(C.surface, 0.4)}" stroke="${col}" stroke-width="1.5"/>`
    + `<circle r="2.4" fill="${col}"/>`
    + `<path d="M0 ${-r - 3.5} V${-r + 1.5} M0 ${r - 1.5} V${r + 3.5} M${-r - 3.5} 0 H${-r + 1.5} M${r - 1.5} 0 H${r + 3.5}" stroke="${col}" stroke-width="1.5" stroke-linecap="round"/></g>`
    + `</svg>`;
}

function calloutHtml(p: CardPlacement | null, c: CardContent, st: CalloutState, C: ColorPalette): string {
  let leader = '';
  if (p && p.mode === 'callout' && p.attach) {
    const ax = p.attach.x - p.anchor.x, ay = p.attach.y - p.anchor.y;
    const pts = leaderPoints(ax, ay);
    leader = `<svg width="1" height="1" style="position:absolute;left:0;top:0;overflow:visible;">`
      + `<polyline class="zc-lead-halo" points="${pts}" fill="none" stroke="rgba(0,0,0,0.4)" stroke-width="3.75" stroke-linejoin="round" stroke-linecap="round"/>`
      + `<polyline class="zc-lead" points="${pts}" fill="none" stroke="${c.borderColor}" stroke-opacity="0.85" stroke-width="1.25" stroke-linejoin="round" stroke-linecap="round"/>`
      + `<rect x="${ax - 2.5}" y="${ay - 2.5}" width="5" height="5" fill="${c.borderColor}"/>`
      + `</svg>`;
  }
  const reticle = p && p.mode === 'tag' ? '' : reticleHtml(c, C);
  const tag = p ? tagHtml(p, c, C) : '';
  // Every zone stays identifiable after its card fades: the name sits under the reticle and steps aside while the card is up.
  const label = `<div class="zc-name" style="position:absolute;left:0;top:13px;transform:translateX(-50%);width:max-content;max-width:120px;text-align:center;font-family:${FONT};font-size:10px;line-height:12px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:${C.text};text-shadow:0 0 3px rgba(0,0,0,0.95),0 1px 2px rgba(0,0,0,0.95);pointer-events:none;">${escapeHtml(c.name)}</div>`;
  // Tags are see-through at rest and dense enough to read over bright basemaps once the zone is hovered or selected.
  const vars = [
    `--zc-color:${c.borderColor}`,
    `--zc-glass:${withAlpha(C.surface, 0.68)}`,
    `--zc-glass-hi:${withAlpha(C.surface, 0.94)}`,
    `--zc-border:${withAlpha(C.border2, 0.7)}`,
    `--zc-border-hi:${withAlpha(c.borderColor, 0.8)}`,
    `--zc-ring:${withAlpha(c.borderColor, 0.8)}`,
  ].join(';');
  return `<div class="crowd-zone-callout ${stateClasses(st)}${p ? ' hc' : ''}" data-zone-callout="${escapeHtml(c.id)}" style="position:absolute;left:0;top:0;width:0;height:0;overflow:visible;pointer-events:none;${vars}">`
    + `<div class="zc-card-group${st.shown ? ' on' : ''}" style="position:absolute;left:0;top:0;width:0;height:0;overflow:visible;pointer-events:none;">${leader}${tag}</div>`
    + `${reticle}${label}</div>`;
}

// ── Layout plumbing ────────────────────────────────────────────────────────────

const SAFE_INSET = 8;

function containerRect(el: Element, box: DOMRect): Rect {
  const r = el.getBoundingClientRect();
  return { x: r.left - box.left, y: r.top - box.top, w: r.width, h: r.height };
}

/** Panels floating over the map: anything tagged data-map-obstacle plus Leaflet's own controls. */
function collectObstacles(map: LeafletMap): Rect[] {
  const container = map.getContainer();
  const box = container.getBoundingClientRect();
  const nodes = [
    ...Array.from(container.parentElement?.querySelectorAll('[data-map-obstacle]') ?? []),
    ...Array.from(container.querySelectorAll('.leaflet-control-container .leaflet-control')),
  ];
  return nodes.map(n => containerRect(n, box)).filter(r => r.w > 0 && r.h > 0);
}

function layoutZones(map: LeafletMap, zones: Zone[], contents: Map<string, CardContent>, measured: Map<string, { w: number; h: number }>): CardZoneInput[] {
  const result: CardZoneInput[] = [];
  for (const zone of zones) {
    if (zone.geometry.type !== 'Polygon') continue;
    const content = contents.get(zone.id);
    if (!content) continue;
    const coords = zone.geometry.coordinates as number[][][];
    const ring: Pt[] = openRing(coords[0]).map(([lng, lat]) => {
      const p = map.latLngToContainerPoint([lat, lng]);
      return { x: p.x, y: p.y };
    });
    const [lat, lng] = polygonCentroid(coords);
    const a = map.latLngToContainerPoint([lat, lng]);
    const estimate = estimateSizes(content);
    result.push({
      id: zone.id,
      ring,
      anchor: { x: a.x, y: a.y },
      sizes: {
        full: measured.get(`${zone.id}:full`) ?? estimate.full,
        compact: measured.get(`${zone.id}:compact`) ?? estimate.compact,
      },
    });
  }
  return result;
}

// ── Layer ──────────────────────────────────────────────────────────────────────

export interface ZoneBoundaryLayerProps {
  zones:          Zone[];
  snapshots:      Record<string, ZoneSnapshot>;
  /** Active (unresolved) alert count per zone id; zones absent from the map have none. */
  alertCounts?:   Record<string, number>;
  selectedZoneId: string | null;
  onZoneClick:    (zone: Zone) => void;
  /** Zones whose tag the operator has closed; their reticle stays. */
  hiddenTagIds?:  ReadonlySet<string>;
  /** false hides every tag (reticles stay). */
  showTags?:      boolean;
  /** Tags fade out this long after the map lands (ms); hover or selection brings one back. 0 keeps every tag up. */
  autoHideMs?:    number;
  /** Keep the cards up and do not start counting yet (the map is still being framed and has not been shown). */
  holdReveal?:    boolean;
  onHideTag?:     (zoneId: string) => void;
  /** Fires once the cards have been placed or have finished fading, so the camera can frame what is actually on screen. */
  onCalloutsSettled?: (revealed: boolean) => void;
  C:              ColorPalette;
}

const NO_HIDDEN: ReadonlySet<string> = new Set();

/** Active alert, or utilisation at the alert threshold. */
function needsAttention(content: CardContent): boolean {
  return content.alerts > 0 || zoneStatusFromUtil(content.util) === 'alert';
}

export function ZoneBoundaryLayer({
  zones, snapshots, alertCounts, selectedZoneId, onZoneClick, hiddenTagIds = NO_HIDDEN, showTags = true, autoHideMs = TAG_AUTO_HIDE_MS, holdReveal = false, onHideTag, onCalloutsSettled, C,
}: ZoneBoundaryLayerProps) {
  const map = useMap();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [fading, setFading] = useState(false);
  const [revealed, setRevealed] = useState(true);
  const [placements, setPlacements] = useState<CardPlacement[]>([]);
  const [measureTick, setMeasureTick] = useState(0);
  const measured = useRef(new Map<string, { w: number; h: number }>());
  const previousSlots = useRef<Record<string, string>>({});

  const contents = useMemo(() => {
    const out = new Map<string, CardContent>();
    for (const zone of zones) {
      const snap = snapshots[zone.id];
      out.set(zone.id, {
        id: zone.id,
        name: zone.name,
        util: zone.archivedAt || !snap ? null : snap.capacityUtilisation,
        persons: zone.archivedAt || !snap ? null : snap.occupancyCount,
        trend: snap?.trendDirection ?? null,
        alerts: alertCounts?.[zone.id] ?? 0,
        bearing: snap?.dominantDirection ?? null,
        borderColor: statusBorderColor(zone, snap, C),
      });
    }
    return out;
  }, [zones, snapshots, alertCounts, C]);

  const taggedZones = useMemo(
    () => (showTags ? zones.filter(zone => !hiddenTagIds.has(zone.id)) : []),
    [zones, hiddenTagIds, showTags],
  );

  // Only what changes a tag's size (not its colours or numbers) needs a fresh layout.
  const sizeSignature = useMemo(() => taggedZones.map(zone => {
    const c = contents.get(zone.id);
    return `${zone.id}:${c?.name}:${c?.util === null ? 'n' : 'v'}`;
  }).join('|'), [taggedZones, contents]);

  const zonesRef = useRef(taggedZones);
  zonesRef.current = taggedZones;
  const allZonesRef = useRef(zones);
  allZonesRef.current = zones;
  const contentsRef = useRef(contents);
  contentsRef.current = contents;

  const relayout = useCallback(() => {
    const size = map.getSize();
    const safe: Rect = { x: SAFE_INSET, y: SAFE_INSET, w: size.x - 2 * SAFE_INSET, h: size.y - 2 * SAFE_INSET };
    const input = layoutZones(map, zonesRef.current, contentsRef.current, measured.current);
    // Zones without a tag still occupy space: keep tags clear of their polygons too.
    const tagged = new Set(zonesRef.current.map(zone => zone.id));
    const blockers = layoutZones(map, allZonesRef.current.filter(zone => !tagged.has(zone.id)), contentsRef.current, measured.current)
      .map(zone => zone.ring);
    const plan = input.length === 0
      ? []
      : solveCardLayout({ zones: input, blockers, safe, obstacles: collectObstacles(map), previous: previousSlots.current });
    previousSlots.current = Object.fromEntries(plan.map(p => [p.zoneId, p.slotKey]));
    setPlacements(plan);
    setFading(false);
  }, [map]);

  useEffect(() => { relayout(); }, [relayout, sizeSignature, measureTick]);

  useEffect(ensureCalloutStyles, []);

  // Landing, a different set of zones (operation switch) or switching the layer on shows every tag, then lets them go.
  const zoneSetKey = useMemo(() => zones.map(zone => zone.id).join('|'), [zones]);
  useEffect(() => {
    setRevealed(true);
    if (holdReveal || !showTags || zoneSetKey === '' || autoHideMs <= 0) return undefined;
    // The count starts once the map and the panel are on screen, after their fade-in has finished.
    const timer = setTimeout(() => setRevealed(false), autoHideMs + REVEAL_FADE_IN_MS);
    return () => clearTimeout(timer);
  }, [zoneSetKey, showTags, autoHideMs, holdReveal]);

  // Tell the camera when the cards are in their final place (after a layout, or once the reveal has faded out).
  const settledRef = useRef(onCalloutsSettled);
  settledRef.current = onCalloutsSettled;
  // A zone that starts (or stops) needing attention gains (or loses) a card that stays up, which changes the picture too.
  const attentionKey = useMemo(
    () => zones.filter(zone => { const c = contents.get(zone.id); return c ? needsAttention(c) : false; }).map(zone => zone.id).join('|'),
    [zones, contents],
  );
  useEffect(() => {
    if (fading) return undefined;
    // Long enough for the cards' fade to finish, so the camera glides only once the picture has stopped changing.
    // (Quicker while the first cards are up: nothing is fading, the cards only need to be placed.)
    const timer = setTimeout(() => settledRef.current?.(revealed), revealed ? 150 : 700);
    return () => clearTimeout(timer);
  }, [placements, revealed, fading]);
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;
  // When the first cards go, the lasting ones go with them, so the camera can glide with nothing on screen to flicker;
  // they come back once the map has settled (a timer covers the case where the camera has nowhere to go).
  const movingRef = useRef(false);
  useEffect(() => {
    const start = () => { movingRef.current = true; };
    const end = () => { movingRef.current = false; };
    map.on('movestart', start); map.on('moveend', end);
    return () => { map.off('movestart', start); map.off('moveend', end); };
  }, [map]);
  const firstReveal = useRef(true);
  useEffect(() => {
    if (revealed) { firstReveal.current = false; return undefined; }
    if (!showTags || firstReveal.current) return undefined;
    setFading(true);
    // Cards gone: let the camera make its one move, then (below) bring the lasting cards back where the map settled.
    const glide = setTimeout(() => settledRef.current?.(false), 650);
    const restore = setTimeout(() => { if (!movingRef.current) relayout(); }, 2400);
    return () => { clearTimeout(glide); clearTimeout(restore); };
  }, [revealed, showTags, relayout]);
  // A zone flickering around the alert threshold must not keep nudging the camera: follow only a change that lasts.
  const firstAttentionKey = useRef(true);
  useEffect(() => {
    if (firstAttentionKey.current) { firstAttentionKey.current = false; return undefined; }
    const timer = setTimeout(() => settledRef.current?.(revealedRef.current), 4000);
    return () => clearTimeout(timer);
  }, [attentionKey]);

  useEffect(() => {
    // Tags hide while a zoom animates. A cancelled fly-to never fires zoomend, so a timer guarantees they come back.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const hide = () => { setFading(true); clearTimeout(timer); timer = setTimeout(relayout, 1500); };
    const settle = () => { clearTimeout(timer); relayout(); };
    map.on('zoomstart', hide);
    map.on('zoomend', settle);
    map.on('moveend', settle);
    map.on('resize', settle);
    return () => {
      clearTimeout(timer);
      map.off('zoomstart', hide); map.off('zoomend', settle); map.off('moveend', settle); map.off('resize', settle);
    };
  }, [map, relayout]);

  // After paint, read each tag's real size; if the estimate was off, solve again with the truth.
  useEffect(() => {
    let changed = false;
    map.getContainer().querySelectorAll<HTMLElement>('[data-zone-card]').forEach(el => {
      const key = `${el.dataset.zoneCard}:${el.dataset.cardSize}`;
      const w = el.offsetWidth, h = el.offsetHeight;
      const prev = measured.current.get(key);
      if (w > 0 && h > 0 && (!prev || Math.abs(prev.w - w) > 2 || Math.abs(prev.h - h) > 2)) {
        measured.current.set(key, { w, h });
        changed = true;
      }
    });
    if (changed) setMeasureTick(t => t + 1);
  }, [map, placements]);

  // The close control and the cards sit inside Leaflet markers, so keep their mouse events away from the map (drag,
  // zoom, click). Clicking a card does exactly what clicking its zone does; hovering one lights its zone.
  const onZoneClickRef = useRef(onZoneClick);
  onZoneClickRef.current = onZoneClick;
  useEffect(() => {
    const container = map.getContainer();
    const closest = (e: Event, selector: string): HTMLElement | null => (e.target as HTMLElement | null)?.closest?.(selector) ?? null;
    const swallow = (e: Event) => { if (closest(e, '[data-zone-close], [data-zone-card]')) e.stopPropagation(); };
    const onClick = (e: Event) => {
      const close = closest(e, '[data-zone-close]');
      if (close) {
        e.stopPropagation();
        if (close.dataset.zoneClose) onHideTag?.(close.dataset.zoneClose);
        return;
      }
      const card = closest(e, '[data-zone-card]');
      if (!card) return;
      e.stopPropagation();
      const zone = allZonesRef.current.find(row => row.id === card.dataset.zoneCard);
      if (zone) onZoneClickRef.current(zone);
    };
    const onOver = (e: Event) => { const card = closest(e, '[data-zone-card]'); if (card) setHoveredId(card.dataset.zoneCard ?? null); };
    const onOut = (e: Event) => { if (closest(e, '[data-zone-card]')) setHoveredId(null); };
    const swallowed = ['mousedown', 'pointerdown', 'touchstart', 'dblclick'];
    swallowed.forEach(type => container.addEventListener(type, swallow, true));
    container.addEventListener('click', onClick, true);
    container.addEventListener('mouseover', onOver, true);
    container.addEventListener('mouseout', onOut, true);
    return () => {
      swallowed.forEach(type => container.removeEventListener(type, swallow, true));
      container.removeEventListener('click', onClick, true);
      container.removeEventListener('mouseover', onOver, true);
      container.removeEventListener('mouseout', onOut, true);
    };
  }, [map, onHideTag]);

  const placementById = useMemo(() => new Map(placements.map(p => [p.zoneId, p])), [placements]);

  return (
    <>
      {zones.map(zone => (
        <ZonePolygon
          detailHtml={contents.has(zone.id) ? hoverHtml(contents.get(zone.id) as CardContent, C) : null}
          key={zone.id}
          zone={zone}
          borderColor={contents.get(zone.id)?.borderColor ?? C.muted}
          isSelected={zone.id === selectedZoneId}
          isHovered={zone.id === hoveredId}
          onZoneClick={onZoneClick}
          onHover={setHoveredId}
        />
      ))}
      {zones.filter(zone => zone.id === selectedZoneId && !zone.archivedAt).map(zone => (
        <SelectedZoneHalo key={`halo-${zone.id}`} zone={zone} color={contents.get(zone.id)?.borderColor ?? C.muted} />
      ))}
      {zones.map(zone => {
        const content = contents.get(zone.id);
        if (!content || zone.geometry.type !== 'Polygon') return null;
        return (
          <ZoneCallout
            key={zone.id}
            zone={zone}
            placement={placementById.get(zone.id) ?? null}
            content={content}
            state={{
              hovered: zone.id === hoveredId,
              selected: zone.id === selectedZoneId,
              // A zone that needs attention keeps its name on the map; calm zones fade after the landing reveal.
              shown: revealed || zone.id === selectedZoneId || needsAttention(content),
              fading,
              labelled: showTags,
            }}
            C={C}
          />
        );
      })}
    </>
  );
}

function ZonePolygon({ zone, borderColor, isSelected, isHovered, onZoneClick, onHover, detailHtml }: {
  zone: Zone; borderColor: string; isSelected: boolean; isHovered: boolean; detailHtml: string | null;
  onZoneClick: (zone: Zone) => void; onHover: (id: string | null) => void;
}) {
  const coords = zone.geometry.type === 'Polygon' ? zone.geometry.coordinates as number[][][] : null;
  const rings = useMemo(() => (coords ? toLeafletRings(coords) : []), [zone.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const handlers = useMemo(() => ({
    click: () => onZoneClick(zone),
    mouseover: () => onHover(zone.id),
    mouseout: () => onHover(null),
  }), [zone, onZoneClick, onHover]);
  if (!coords) return null;

  return (
    <Polygon
      positions={rings}
      pathOptions={{
        color:       borderColor,
        fillColor:   isHovered ? borderColor : '#ffffff',
        fillOpacity: isHovered ? 0.12 : 0.01,   // near-zero fill so the interior still catches pointer events
        weight:      isSelected || isHovered ? 3 : 1.5,
        opacity:     1,
        dashArray:   isSelected || isHovered ? undefined : '4,3',
        className:   `crowd-zone-polygon zp-${zone.id}`,
      }}
      eventHandlers={handlers}
    >
      {detailHtml && (
        <Tooltip sticky direction="auto" offset={[16, 12]} className="crowd-hover-tip" opacity={1}>
          <div dangerouslySetInnerHTML={{ __html: detailHtml }} />
        </Tooltip>
      )}
    </Polygon>
  );
}

/** Gap between the zone boundary and its halo, in screen pixels. */
const HALO_GAP_PX = 4;

/** The ring pushed outwards by `distance` px (mitred corners), so a stroke on it never reaches into the zone. */
function offsetRingOutward(points: L.Point[], distance: number): L.Point[] {
  const n = points.length;
  if (n < 3) return points;
  let area = 0;
  for (let i = 0; i < n; i++) { const p = points[i], q = points[(i + 1) % n]; area += p.x * q.y - q.x * p.y; }
  const side = area >= 0 ? 1 : -1;
  const normal = (a: L.Point, b: L.Point) => {
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
    return { x: side * dy / len, y: -side * dx / len };
  };
  return points.map((p, i) => {
    const n1 = normal(points[(i + n - 1) % n], p), n2 = normal(p, points[(i + 1) % n]);
    const k = Math.min(distance / Math.max(1 + n1.x * n2.x + n1.y * n2.y, 0.35), distance * 3);
    return L.point(p.x + (n1.x + n2.x) * k, p.y + (n1.y + n2.y) * k);
  });
}

/**
 * Marks the zone shown in the right-hand panel without touching the zone itself: a faint glow that sits just outside
 * its boundary and breathes slowly, and an even fainter ring that contracts onto the boundary. Both are separate,
 * non-interactive overlays, so the zone's own polygon, hover and click are untouched.
 */
function SelectedZoneHalo({ zone, color }: { zone: Zone; color: string }) {
  const map = useMap();
  const coords = zone.geometry.type === 'Polygon' ? zone.geometry.coordinates as number[][][] : null;
  // The gap is fixed in screen pixels, so the outline is rebuilt when the zoom changes.
  const [zoom, setZoom] = useState(() => map.getZoom());
  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [map]);
  const outline = useMemo(() => {
    if (!coords) return [];
    const ring = openRing(coords[0]).map(([lng, lat]) => map.latLngToLayerPoint([lat, lng]));
    return offsetRingOutward(ring, HALO_GAP_PX).map(p => map.layerPointToLatLng(p));
  }, [coords, map, zoom]);  // eslint-disable-line react-hooks/exhaustive-deps
  const glowRef = useRef<L.Polygon | null>(null);
  const ringRef = useRef<L.Polygon | null>(null);
  // The classes are added to the live SVG paths: a className passed through pathOptions is not reliably applied
  // once the layer exists (Leaflet reads it only when the path is first created).
  useEffect(() => {
    const glow = glowRef.current?.getElement() as SVGElement | undefined;
    const ring = ringRef.current?.getElement() as SVGElement | undefined;
    glow?.classList.add('crowd-zone-sel-glow');
    glow?.style.setProperty('--zs-color', color);
    ring?.classList.add('crowd-zone-sel-ring');
  }, [color, zone.id, outline]);
  if (!coords) return null;
  return (
    <>
      <Polygon ref={glowRef} positions={outline} interactive={false} pathOptions={{ color, weight: 1.25, opacity: 0.45, fill: false }} />
      <Polygon ref={ringRef} positions={outline} interactive={false} pathOptions={{ color, weight: 1, opacity: 0.5, fill: false }} />
    </>
  );
}

function ZoneCallout({ zone, placement, content, state, C }: {
  zone: Zone; placement: CardPlacement | null; content: CardContent; state: CalloutState; C: ColorPalette;
}) {
  const centroid = useMemo(
    () => polygonCentroid((zone.geometry as { coordinates: number[][][] }).coordinates),
    [zone.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // State is read when the markup is built but is not a dependency: hover/selection/reveal change classes on the live
  // element (below) so CSS can animate them, and only content or placement changes replace the icon.
  const stateRef = useRef(state);
  stateRef.current = state;
  const html = useMemo(
    () => calloutHtml(placement, content, stateRef.current, C),
    [placement, content, C],
  );
  const icon = useMemo(
    () => L.divIcon({ className: '', html, iconSize: [0, 0], iconAnchor: [0, 0] }),
    [html],
  );
  const markerRef = useRef<L.Marker | null>(null);
  useEffect(() => {
    const root = markerRef.current?.getElement()?.querySelector<HTMLElement>('.crowd-zone-callout');
    if (!root) return;
    root.classList.toggle('hot', state.hovered || state.selected);
    root.classList.toggle('sel', state.selected);
    root.classList.toggle('zoom', state.fading);
    root.classList.toggle('nolabel', !state.labelled);
    root.querySelector('.zc-card-group')?.classList.toggle('on', state.shown);
  }, [state.hovered, state.selected, state.shown, state.fading, state.labelled, icon]);
  const zIndexOffset = state.hovered ? 1000 : state.selected ? 600 : 100;
  return <Marker ref={markerRef} position={centroid} icon={icon} interactive={false} keyboard={false} zIndexOffset={zIndexOffset} />;
}
